import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import type { Book } from '../../../shared/types/index.js';
import { BookSynopsisCard } from '../../../shared/components/BookSynopsisCard.js';
import { BookTableOfContentsCard } from '../../../shared/components/BookTableOfContentsCard.js';
import { useAuthStore } from '../../auth/store/useAuthStore.js';
import { useNotificationStore } from '../../notifications/store/useNotificationStore.js';
import { api } from '../../../shared/api/api.js';
import { CertificateModal } from '../../uibn/components/CertificateModal.js';
import { EditionTreeModal } from '../../editions/components/EditionTreeModal.js';
import { CreateEditionModal } from '../../editions/components/CreateEditionModal.js';
import { EmbedBadgeModal } from '../../badges/components/EmbedBadgeModal.js';
import { BookVerificationModal } from '../../verification/components/BookVerificationModal.js';
import { PublisherVerificationModal } from './book-list/PublisherVerificationModal.js';
import { BookLabelPrintModal } from './BookLabelPrintModal.js';
import { downloadCertificatePdf } from '../../uibn/utils/certificateGenerator.js';
import { canEditBookMetadata } from './book-list/bookListUtils.js';
import { SeoHead, useBookOgImage, createBookSchema } from '../../../shared/components/index.js';
import { slugify } from '../../../shared/utils/stringUtils.js';
import { useBodyScrollLock } from '../../../shared/utils/scrollLock.js';
import {
  BookDetailHeader,
  BookDetailHero,
  BookDetailEditionBanner,
  BookDetailBarcodeSection,
  BookDetailAdminStatusControl,
} from './book-detail/index.js';

interface BookDetailModalProps {
  book: Book | null;
  onClose: () => void;
  onBookUpdated?: (updatedBook: Book) => void;
  onEditRequested?: (book: Book) => void;
  onDeleteRequested?: (id: string, title: string) => void;
}

export const BookDetailModal: React.FC<BookDetailModalProps> = ({
  book,
  onClose,
  onBookUpdated,
  onEditRequested,
  onDeleteRequested,
}) => {
  const { user, openLoginModal } = useAuthStore();
  const [activeCodeTab, setActiveCodeTab] = useState<'barcode' | 'qr'>('barcode');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusNote, setStatusNote] = useState('');
  const [isCertModalOpen, setIsCertModalOpen] = useState(false);
  const [isQuickDownloading, setIsQuickDownloading] = useState(false);
  const [isEditionTreeOpen, setIsEditionTreeOpen] = useState(false);
  const [isCreateEditionOpen, setIsCreateEditionOpen] = useState(false);
  const [isEmbedBadgeOpen, setIsEmbedBadgeOpen] = useState(false);
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [isPublisherVerifyOpen, setIsPublisherVerifyOpen] = useState(false);
  const [isPrintLabelOpen, setIsPrintLabelOpen] = useState(false);
  const [currentDisplayBook, setCurrentDisplayBook] = useState<Book | null>(book);

  useEffect(() => {
    setCurrentDisplayBook(book);
  }, [book]);

  const activeBook = currentDisplayBook || book;

  const handleOpenPrintLabel = () => {
    console.log('[PrintFlow] Opening print label modal for book:', activeBook?.uibn);
    setIsPrintLabelOpen(true);
  };

  const bookOgImage = useBookOgImage(
    activeBook
      ? {
          title: activeBook.title,
          uibn: activeBook.uibn,
          authorName: activeBook.authorName,
          publisherName: activeBook.publisherName,
          category: activeBook.category,
          publicationYear: activeBook.publicationYear,
          coverUrl: activeBook.coverUrl || undefined,
        }
      : null
  );

  const bookStructuredData = React.useMemo(() => {
    if (!activeBook) return undefined;
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://uibn.id';
    const bookUrl = `${origin}/buku/${slugify(activeBook.title)}`;
    return createBookSchema({
      title: activeBook.title,
      uibn: activeBook.uibn,
      authorName: activeBook.authorName,
      publisherName: activeBook.publisherName,
      category: activeBook.category,
      publicationYear: activeBook.publicationYear,
      pages: activeBook.pages || undefined,
      language: activeBook.language || undefined,
      synopsis: activeBook.synopsis || undefined,
      coverUrl: activeBook.coverUrl || undefined,
      url: bookUrl,
    });
  }, [activeBook]);

  const bookUrl = activeBook ? `${typeof window !== 'undefined' ? window.location.origin : ''}/buku/${slugify(activeBook.title)}` : undefined;

  useBodyScrollLock(Boolean(activeBook));

  // Register Esc key listener
  useEffect(() => {
    if (!activeBook) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeBook, onClose]);

  if (!activeBook) return null;

  const handleQuickDownloadCert = async () => {
    if (!user) {
      openLoginModal();
      return;
    }
    setIsQuickDownloading(true);
    try {
      await downloadCertificatePdf(activeBook);
    } catch (err) {
      console.error('Error downloading certificate:', err);
    } finally {
      setIsQuickDownloading(false);
    }
  };

  const isAuthorOwner = Boolean(
    user?.role === 'penulis' &&
      (activeBook.authorId === user.id || activeBook.authorName.toLowerCase() === user.name.toLowerCase())
  );
  const isPublisherOwner = Boolean(
    user?.role === 'penerbit' &&
      (activeBook.publisherId === user.id ||
        (user.organization && activeBook.publisherName.toLowerCase() === user.organization.toLowerCase()) ||
        activeBook.publisherName.toLowerCase() === user.name.toLowerCase() ||
        (user.publisherCode && activeBook.uibn.includes(user.publisherCode)))
  );
  const isAdmin = user?.role === 'admin';

  const canEdit = canEditBookMetadata(activeBook, user);
  const canManageStatus = Boolean(isAdmin || (isPublisherOwner && activeBook.status === 'menunggu_penerbit'));

  const handleStatusChange = async (newStatus: string, customNote?: string) => {
    setIsUpdatingStatus(true);
    try {
      const finalNote = customNote || statusNote || undefined;
      const res = await api.updateBookStatus(activeBook.id, newStatus, finalNote);
      if (onBookUpdated) {
        onBookUpdated(res.book);
      }
      setStatusNote('');
      setIsPublisherVerifyOpen(false);
    } catch (err: any) {
      useNotificationStore.getState().showToast('Gagal Mengubah Status', err.message || 'Gagal mengubah status buku', 'error');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const modalContent = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto overscroll-contain"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {activeBook && (
        <SeoHead
          title={`${activeBook.title} – Nomor Registrasi UIBN ${activeBook.uibn}`}
          description={activeBook.synopsis?.replace(/<[^>]*>/g, '').substring(0, 160) || `Informasi detail naskah buku ${activeBook.title} oleh ${activeBook.authorName}. Terdaftar resmi dengan nomor UIBN ${activeBook.uibn}.`}
          ogType="book"
          ogImage={bookOgImage || activeBook.coverUrl || undefined}
          canonicalUrl={bookUrl}
          keywords={[activeBook.title, activeBook.uibn, activeBook.authorName, activeBook.publisherName, 'katalog UIBN', 'buku terdaftar']}
          structuredData={bookStructuredData}
        />
      )}

      <motion.div
        initial={{ scale: 0.95, y: 15, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.95, y: 15, opacity: 0 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        className="bg-white rounded-2xl max-w-2xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl w-full max-h-[92vh] my-auto overflow-hidden shadow-2xl border border-slate-100 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 1. Modal Header */}
        <BookDetailHeader
          book={activeBook}
          user={user}
          canEdit={canEdit}
          onClose={onClose}
          onOpenEditionTree={() => setIsEditionTreeOpen(true)}
          onOpenCertificateModal={() => setIsCertModalOpen(true)}
          onOpenPrintLabel={handleOpenPrintLabel}
          onEditRequested={onEditRequested}
          onDeleteRequested={onDeleteRequested ? (b) => onDeleteRequested(b.id, b.title) : undefined}
        />

        {/* Modal Body */}
        <div className="p-5 sm:p-6 lg:p-8 2xl:p-10 overflow-y-auto space-y-6 lg:space-y-8 flex-1">
          {/* 2. Main Book Hero & Details */}
          <BookDetailHero book={activeBook} />

          {/* 3. Edition Tree Lineage Banner */}
          <BookDetailEditionBanner
            book={activeBook}
            canEdit={canEdit}
            onOpenEditionTree={() => setIsEditionTreeOpen(true)}
            onOpenCreateEdition={() => setIsCreateEditionOpen(true)}
          />

          {/* 4. Barcode, QR Code & Certificate/Widget Banners */}
          <BookDetailBarcodeSection
            book={activeBook}
            canEdit={canEdit}
            activeCodeTab={activeCodeTab}
            isQuickDownloading={isQuickDownloading}
            onCodeTabChange={setActiveCodeTab}
            onOpenCertificateModal={() => setIsCertModalOpen(true)}
            onQuickDownloadCert={handleQuickDownloadCert}
            onOpenPrintLabel={handleOpenPrintLabel}
            onOpenVerifyModal={() => setIsVerifyModalOpen(true)}
            onOpenEmbedBadgeModal={() => setIsEmbedBadgeOpen(true)}
          />

          {/* 5. Professional Synopsis & Narrative Overview */}
          <BookSynopsisCard
            synopsis={activeBook.synopsis}
            bookTitle={activeBook.title}
            defaultExpanded={true}
            showCopy={canEdit}
          />

          {/* 5b. Book Table of Contents (Daftar Isi) */}
          {activeBook.tableOfContents && (
            <BookTableOfContentsCard
              tableOfContents={activeBook.tableOfContents}
              bookTitle={activeBook.title}
              defaultExpanded={true}
              showCopy={canEdit}
            />
          )}

          {/* 6. Status Control for Admin Only */}
          {canManageStatus && (
            <BookDetailAdminStatusControl
              book={activeBook}
              user={user}
              isAdmin={isAdmin}
              isPublisherOwner={isPublisherOwner}
              statusNote={statusNote}
              isUpdatingStatus={isUpdatingStatus}
              onStatusNoteChange={setStatusNote}
              onStatusChange={handleStatusChange}
              onOpenPublisherVerifyModal={() => setIsPublisherVerifyOpen(true)}
            />
          )}
        </div>
      </motion.div>

      {/* Sub-modals */}
      <CertificateModal
        book={activeBook}
        isOpen={isCertModalOpen}
        onClose={() => setIsCertModalOpen(false)}
      />

      <EditionTreeModal
        book={activeBook}
        isOpen={isEditionTreeOpen}
        onClose={() => setIsEditionTreeOpen(false)}
        onSelectBook={(selected) => {
          setCurrentDisplayBook(selected);
          if (onBookUpdated) onBookUpdated(selected);
        }}
        onBookCreated={(newBook) => {
          if (onBookUpdated) onBookUpdated(newBook);
          setCurrentDisplayBook(newBook);
        }}
      />

      <CreateEditionModal
        parentBook={activeBook}
        isOpen={isCreateEditionOpen}
        onClose={() => setIsCreateEditionOpen(false)}
        onEditionCreated={(newBook) => {
          if (onBookUpdated) onBookUpdated(newBook);
          setCurrentDisplayBook(newBook);
          setIsEditionTreeOpen(true);
        }}
      />

      <EmbedBadgeModal
        book={activeBook}
        isOpen={isEmbedBadgeOpen}
        onClose={() => setIsEmbedBadgeOpen(false)}
        onOpenVerifyModal={() => {
          setIsEmbedBadgeOpen(false);
          setIsVerifyModalOpen(true);
        }}
      />

      <BookVerificationModal
        uibnOrId={activeBook.uibn}
        isOpen={isVerifyModalOpen}
        onClose={() => setIsVerifyModalOpen(false)}
        onOpenEditionTree={() => {
          setIsVerifyModalOpen(false);
          setIsEditionTreeOpen(true);
        }}
        onOpenEmbedModal={() => {
          setIsVerifyModalOpen(false);
          setIsEmbedBadgeOpen(true);
        }}
      />

      <PublisherVerificationModal
        isOpen={isPublisherVerifyOpen}
        onClose={() => setIsPublisherVerifyOpen(false)}
        onConfirm={(verifNote) => handleStatusChange('diajukan', verifNote)}
        book={activeBook}
        isPending={isUpdatingStatus}
      />

      <BookLabelPrintModal
        book={activeBook}
        isOpen={isPrintLabelOpen}
        onClose={() => setIsPrintLabelOpen(false)}
      />
    </motion.div>
  );

  return createPortal(modalContent, document.body);
};
